import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import type { UserRole } from '../../auth/types/auth.types';
import type {
  AlcanceNegocioAsignable,
  AsignarPerfilRespuesta,
  Cadena,
  Perfil,
  Responsable,
} from '../../negocio/negocio.types';
import { PerfilesService } from '../services/perfiles.service';
import {
  agregarAlcance,
  CADENAS,
  ETIQUETA_MODULO,
  opcionesDeResponsables,
  ordenarModulos,
  quitarAlcance,
} from '../utils/perfiles';
import { admitePerfil, etiquetaAlcanceNegocio } from '../../users/utils/sesiones';
import { mensajeDeError } from '../../negocio/errores';
import { CLASE_CAMPO } from './PerfilDialog';

export interface CuentaParaPerfil {
  id: string;
  email: string;
  role: UserRole | string;
  perfil?: Pick<Perfil, 'id' | 'clave' | 'nombre'> | null;
  alcancesNegocio?: AlcanceNegocioAsignable[];
}

interface Props {
  usuario: CuentaParaPerfil;
  perfiles: Perfil[];
  /** Responsables vigentes: de ahí salen los regionales, supervisores y tiendas que la API acepta. */
  responsables: Responsable[];
  onGuardado: (r: AsignarPerfilRespuesta) => void;
}

type TipoAlcance = AlcanceNegocioAsignable['tipo'];

const TIPOS: Array<{ value: TipoAlcance; label: string }> = [
  { value: 'todas', label: 'Todas las tiendas' },
  { value: 'cadena', label: 'Cadena' },
  { value: 'regional', label: 'Regional' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'tienda', label: 'Tienda' },
];

const ROL_SIN_PERFIL =
  'Esta cuenta no lleva perfil: las cuentas de tienda y bodega, vendedor y consulta se quedan en Venta Asistida.';

/**
 * Perfil y alcance de Consulta CM de UNA cuenta (fase 3). Un solo
 * `PUT /users/:id/perfil` reemplaza el perfil y TODO `perfil_alcance`.
 *
 * El alcance de aquí sólo lo lee Consulta CM: dar «regional de X» deja ver
 * sus tiendas, pero no autoriza nada en Metas (eso se da en Metas › Accesos,
 * y la API lo avisa).
 */
export const AsignarPerfilForm: React.FC<Props> = ({ usuario, perfiles, responsables, onGuardado }) => {
  const [perfilId, setPerfilId] = useState(usuario.perfil?.id ?? '');
  const [alcances, setAlcances] = useState<AlcanceNegocioAsignable[]>(usuario.alcancesNegocio ?? []);
  const [tipo, setTipo] = useState<TipoAlcance>('todas');
  const [valor, setValor] = useState('');
  const [avisos, setAvisos] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    setPerfilId(usuario.perfil?.id ?? '');
    setAlcances(usuario.alcancesNegocio ?? []);
    setAvisos([]);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario.id]);

  const opciones = useMemo(() => opcionesDeResponsables(responsables), [responsables]);

  const valores = useMemo((): Array<{ value: string; label: string }> => {
    switch (tipo) {
      case 'cadena':
        return CADENAS.map((c) => ({ value: c, label: c }));
      case 'regional':
        return opciones.regionales.map((n) => ({ value: n, label: n }));
      case 'supervisor':
        return opciones.supervisores.map((n) => ({ value: n, label: n }));
      case 'tienda':
        return opciones.tiendas.map((t) => ({ value: `${t.value.cadena}-${t.value.num}`, label: t.label }));
      default:
        return [];
    }
  }, [tipo, opciones]);

  useEffect(() => {
    setValor(valores[0]?.value ?? '');
  }, [valores]);

  if (!admitePerfil(usuario.role)) {
    return <Message severity="info" text={ROL_SIN_PERFIL} className="w-full justify-start" />;
  }

  // Los inactivos no se asignan (409 PERFIL_INACTIVO); el actual se muestra aunque lo esté.
  const ofrecidos = perfiles.filter((p) => p.activo || p.id === usuario.perfil?.id);
  const elegido = perfiles.find((p) => p.id === perfilId) ?? null;

  const armar = (): AlcanceNegocioAsignable | null => {
    if (tipo === 'todas') return { tipo: 'todas' };
    if (!valor) return null;
    if (tipo === 'cadena') return { tipo: 'cadena', cadena: valor as Cadena };
    if (tipo === 'regional') return { tipo: 'regional', nombre: valor };
    if (tipo === 'supervisor') return { tipo: 'supervisor', nombre: valor };
    const [cadena, num] = valor.split('-');
    return { tipo: 'tienda', cadena: cadena as Cadena, num: Number(num) };
  };

  const agregar = () => {
    const a = armar();
    if (a) setAlcances((xs) => agregarAlcance(xs, a));
  };

  const guardar = async () => {
    setOcupado(true);
    setError(null);
    setAvisos([]);
    try {
      const r = await PerfilesService.asignar(usuario.id, { perfilId: perfilId || null, alcancesNegocio: alcances });
      setAvisos(r.avisos ?? []);
      onGuardado(r);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  };

  const idBase = `asignar-perfil-${usuario.id}`;

  return (
    <div className="space-y-4 text-sm text-gray-700">
      <label className="block font-medium" htmlFor={`${idBase}-perfil`}>
        Perfil
        <select
          id={`${idBase}-perfil`}
          className={CLASE_CAMPO}
          value={perfilId}
          onChange={(e) => setPerfilId(e.target.value)}
          disabled={ocupado}
        >
          <option value="">Sin perfil</option>
          {ofrecidos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
              {p.activo ? '' : ' (desactivado)'}
            </option>
          ))}
        </select>
      </label>
      {elegido ? (
        <div className="flex flex-wrap gap-1">
          {ordenarModulos(elegido.modulos).map((m) => (
            <span key={m} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-800">
              {ETIQUETA_MODULO[m]}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          Sin perfil no entra a Consulta CM (salvo superadmin). En el admin todo sigue como hoy.
        </p>
      )}

      <fieldset className="space-y-2">
        <legend className="font-medium">Alcance en Consulta CM</legend>
        <p className="text-xs text-gray-500">
          Se suma a su alcance de Metas. Sólo deja ver tiendas en Consulta CM: no autoriza nada en Metas.
        </p>
        {alcances.length === 0 ? (
          <p className="text-xs text-gray-500">Sin alcance propio de Consulta CM.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {alcances.map((a, i) => {
              const et = etiquetaAlcanceNegocio(a);
              return (
                <li key={`${et}-${i}`} className="flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-3 pr-1">
                  <span>{et}</span>
                  <button
                    type="button"
                    aria-label={`Quitar ${et}`}
                    title={`Quitar ${et}`}
                    className="rounded-full px-1 text-gray-500 hover:bg-gray-200 hover:text-red-700"
                    onClick={() => setAlcances((xs) => quitarAlcance(xs, i))}
                    disabled={ocupado}
                  >
                    <i className="pi pi-times text-xs" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <select
            id={`${idBase}-tipo`}
            aria-label="Tipo de alcance"
            className={`${CLASE_CAMPO} sm:w-48`}
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoAlcance)}
            disabled={ocupado}
          >
            {TIPOS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {tipo !== 'todas' && (
            <select
              id={`${idBase}-valor`}
              aria-label="Valor del alcance"
              className={`${CLASE_CAMPO} min-w-[12rem] flex-1`}
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              disabled={ocupado || valores.length === 0}
            >
              {valores.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          )}
          <Button
            type="button"
            label="Agregar alcance"
            icon="pi pi-plus"
            className="p-button-outlined p-button-sm"
            onClick={agregar}
            disabled={ocupado || (tipo !== 'todas' && !valor)}
          />
        </div>
      </fieldset>

      <Button
        type="button"
        label="Guardar perfil y alcance"
        icon="pi pi-save"
        onClick={() => void guardar()}
        loading={ocupado}
        disabled={ocupado}
      />
      {avisos.map((a) => (
        <Message key={a} severity="warn" text={a} className="w-full justify-start" />
      ))}
      {error && <Message severity="error" text={error} className="w-full justify-start" />}
    </div>
  );
};
